import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CMSLanguageService, Page, TableContext } from '@markant-platform/shared/data-access';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { AuthenticatedUserService, UserService } from '@markant-platform/shared/user/data-access';
import { BehaviorSubject, catchError, distinctUntilChanged, map, Observable, of, switchMap, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { buildFilterQueryParameter, Filter, mapActiveToMarkantFilter } from '@markant-platform/shared/ui-filter';
import { Table, TableColumnSelectComponent, TableComponent } from '@markant-platform/shared/table/feature';
import { EMPTY_PAGE, TableDraft, TableDraftBuilder } from '@markant-platform/shared/table/model';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import {
  CartridgeVariantAlternativeOwnerResponseDto,
  CartridgeVariantCurrentOwnersResponseDto,
  compareRoleWithOrganizationRoleFirst,
  OrganizationUserDTO,
  StaticRole,
  UserOrganizationStatus,
} from '@markant-platform/shared/user/model';
import {
  DEFAULT_FILTER,
  DEFAULT_HIDDEN_COLUMNS,
  DEFAULT_SORT,
  ORGANIZATION_MEMBER_TABLE_CELLS,
} from './configuration/organization-member-overview.config';
import { deepEqual, MarkantSelectOption } from '@markant-platform/shared/util-helpers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { MarkantElementsModule, MarkantFilterGroup } from '@markant/elements-angular';
import { UiPageHeaderComponent } from '@markant-platform/shared/ui-components';
import { DefaultValuePipe } from '@markant-platform/shared/util-pipes';
import { ModalBridgeComponent } from '@markant-platform/shared/ui-modal';
import { SharedOrganizationFeatureModule } from '../shared-organization-feature.module';
import { SharedDialogFeatureModule } from '@markant-platform/shared/dialog/feature';
import { CommonModule } from '@angular/common';
import { BackofficeCartridgeService } from '@markant-platform/backoffice/data-access';
import { OrganizationMemberInvitationComponent } from '../organization-member-invitation/organization-member-invitation.component';
import { OrganizationStatus, QualificationLevel } from 'data-model';

@Component({
  selector: 'markant-platform-organization-member-overview',
  templateUrl: './organization-member-overview.component.html',
  styleUrls: ['./organization-member-overview.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    UiPageHeaderComponent,
    TranslocoPipe,
    MarkantElementsModule,
    DefaultValuePipe,
    TableComponent,
    ModalBridgeComponent,
    SharedOrganizationFeatureModule,
    SharedDialogFeatureModule,
    TableColumnSelectComponent,
    OrganizationMemberInvitationComponent,
  ],
})
export class OrganizationMemberOverviewComponent extends Table implements OnInit {
  @Input() isDemo = false;

  @ViewChild(TableComponent) tableComponent!: TableComponent;
  @ViewChild(MarkantFilterGroup) filterGroup!: MarkantFilterGroup;

  isInviteDialogOpen = false;
  isRemoveDialogOpen = false;
  isWarningUserDialogOpen = false;
  isOrganizationAdmin = false;
  organizationId!: string;
  organizationStatus?: OrganizationStatus | null;
  organizationQualificationLevel?: QualificationLevel | null;
  isCurrentUserOrganization$ = of(false);
  userRoles: string[] = [];
  userId?: string;
  usersToRemove: { userId: string; version: number }[] = [];
  selectedUserIds: Map<string, number> = new Map<string, number>();
  owners: CartridgeVariantCurrentOwnersResponseDto[] = [];
  alternativeOwners: CartridgeVariantAlternativeOwnerResponseDto[] = [];
  forceReload$ = new BehaviorSubject<boolean>(false);

  protected filter: Filter<MarkantSelectOption> = this.getOAFilter();

  protected readonly DEFAULT_HIDDEN_COLUMNS = DEFAULT_HIDDEN_COLUMNS;

  private readonly goTo = (id: string): void => {
    this.router.navigate([id], { relativeTo: this.route });
  };

  override totalCount = 0;
  override preSelectedOptions = DEFAULT_FILTER;
  override tableDraft: TableDraft = new TableDraftBuilder(ORGANIZATION_MEMBER_TABLE_CELLS)
    .checkboxes()
    .sortableColumns(['firstname', 'lastname'])
    .hiddenColumns(DEFAULT_HIDDEN_COLUMNS)
    .sort(DEFAULT_SORT)
    .actions([
      {
        icon: 'm_arrow-right',
        colorScheme: 'dark',
        size: '11',
        function: this.goTo.bind(this) as (...args: unknown[]) => unknown,
      },
    ])
    .build();

  constructor(
    private readonly userService: UserService,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly route: ActivatedRoute,
    private readonly snackBarService: SnackbarService,
    private readonly organizationService: OrganizationService,
    private readonly router: Router,
    private readonly cmsLanguageService: CMSLanguageService,
    private readonly translocoService: TranslocoService,
    private readonly backofficeCartridgeService: BackofficeCartridgeService
  ) {
    super(TableContext.ORGANIZATION_MEMBER_OVERVIEW);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.organizationId = this.route.snapshot.paramMap.get('organizationId') ?? '';
    this.userId = this.authenticatedUserService.userId;
    this.isCurrentUserOrganization$ = this.authenticatedUserService
      .getOrganizationId()
      .pipe(map((orgId: string) => orgId === this.organizationId));

    if (this.isDemo) {
      this.tableParams$.next({
        ...this.tableParams$.getValue(),
        activeFilter: { state: [UserOrganizationStatus.ACTIVE] },
      });
    }

    this.cmsLanguageService.activeCMSLanguage$
      .pipe(
        switchMap(_ => this.forceReload$),
        switchMap(_ => this.getRequesterRoles()),
        switchMap(_ => this.setOrganizationIdFromTokenIfNotAlreadySet()),
        switchMap(organizationId => this.getOrganizationStatus(organizationId)),
        switchMap(_ => this.setFilterOptions()),
        switchMap(_ => this.tableParams$.pipe(distinctUntilChanged(deepEqual))),
        tap(({ sort }) => {
          this.isLoading$.next(true);
          if (sort) {
            this.tableDraft.sortStartColumnId = sort.sortField;
            this.tableDraft.sortStartDirection = sort.sortOrder;
          }
        }),
        switchMap(({ page: { index, size }, activeFilter, searchQuery, sort }) => {
          const isAllowed = this.isAllowedRoles(this.userRoles);

          if (this.organizationId && isAllowed) {
            this.isOrganizationAdmin = this.userRoles.includes(StaticRole.ORGANIZATION_ADMIN);

            return this.userService.getUsersInOrganizationAsOrganizationUserDto(
              this.organizationId,
              index - 1,
              size,
              sort,
              searchQuery,
              buildFilterQueryParameter(['state', 'role', 'has-role-for-service', 'no-role-for-service'], activeFilter),
              true
            );
          }

          return of(EMPTY_PAGE);
        }),
        catchError(() => {
          this.snackBarService.showErrorSnackbarWithTransloco('common.insufficientRolesError');
          return of(EMPTY_PAGE);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(page => {
        const newResults = (page as Page<OrganizationUserDTO>).result.map(user => ({
          ...user,
          platformRoles: user.platformRoles?.sort(compareRoleWithOrganizationRoleFirst) ?? [],
        }));

        this.totalCount = (page as Page<OrganizationUserDTO>).totalCount;

        const activeFilters = mapActiveToMarkantFilter(
          this.tableParams$.value.activeFilter,
          this.filter,
          this.translocoService
        );
        this.filterGroup.updateActiveFilters(() => activeFilters).then();

        this.reload(newResults);
      });
  }

  private getRequesterRoles(): Observable<string[]> {
    return this.authenticatedUserService.getUserRoles().pipe(
      map(roles => roles.map(({ name }) => name)),
      tap(roles => {
        this.userRoles = roles;
      })
    );
  }

  openInviteDialog(): void {
    this.isInviteDialogOpen = true;
  }

  onSelectionChange(selectedRows: Map<string, number>): void {
    this.selectedUserIds = selectedRows;
  }

  invitationDialogClosed(reload: boolean): void {
    this.isInviteDialogOpen = false;

    if (reload) {
      this.forceReload$.next(true);
    }
  }

  clearAndReload(): void {
    this.selectedUserIds.clear();
    this.tableComponent.clearRowSelection();
    this.usersToRemove = [];
    this.forceReload$.next(true);
  }

  openRemoveDialog(): void {
    if (!this.selectedUserIds.size) {
      this.snackBarService.showErrorSnackbarWithTransloco('organization.users.noUsersSelectedError');
      return;
    }

    if (this.userId && this.selectedUserIds.get(this.userId)) {
      this.snackBarService.showErrorSnackbarWithTransloco('organization.users.ownUserOnRemoveSelectedError');
      this.unSelectUserId(this.userId);
      return;
    }

    this.usersToRemove = Array.from(this.selectedUserIds.entries()).map(([userId, value]) => ({
      userId,
      version: +value,
    }));

    this.isRemoveDialogOpen = true;
  }

  closeRemoveDialogOpen(): void {
    this.isRemoveDialogOpen = false;
  }

  closeWarningUserDialog(): void {
    this.isWarningUserDialogOpen = false;
  }

  private setOrganizationIdFromTokenIfNotAlreadySet(): Observable<string | null> {
    if (this.organizationId) {
      return of(this.organizationId);
    }

    return this.authenticatedUserService.getOrganizationId().pipe(
      map(organizationId => (!organizationId.trim().length ? null : organizationId)),
      tap(organizationId => (this.organizationId = organizationId ?? ''))
    );
  }

  private getOrganizationStatus(
    organizationId: string | null
  ): Observable<{ status: OrganizationStatus | undefined; qualificationLevel: QualificationLevel | null }> {
    if (organizationId === null) {
      return of();
    }

    return this.organizationService.getOrganizationDetailsById(organizationId).pipe(
      map(({ status, qualificationLevel }) => ({
        status,
        qualificationLevel,
      })),
      tap(({ status, qualificationLevel }) => {
        this.organizationStatus = status;
        this.organizationQualificationLevel = qualificationLevel;
      })
    );
  }

  private unSelectUserId(id: string): void {
    const versionUser = this.selectedUserIds.get(id) ?? 0;
    this.tableComponent.toggleSelect(id, versionUser);
  }

  private isAllowedRoles(userRoles: string[]): boolean {
    const allowedRoles = [StaticRole.PLATFORM_ADMIN, StaticRole.ORGANIZATION_ADMIN, StaticRole.ORGANIZATION_MANAGER];

    return userRoles.some(role => allowedRoles.includes(role as StaticRole));
  }

  private getOrganizationStatusFilter(): MarkantSelectOption[] {
    return [
      {
        value: UserOrganizationStatus.ACTIVE,
        representation: this.translocoService.translate(`tables.filter.state.${UserOrganizationStatus.ACTIVE}`),
      },
      {
        value: UserOrganizationStatus.INVITED,
        representation: this.translocoService.translate(`tables.filter.state.${UserOrganizationStatus.INVITED}`),
      },
    ];
  }

  private getOAFilter(): Filter<MarkantSelectOption> {
    return {
      state: {
        translationKey: 'organization.users.status.label',
        filterOptions: this.getOrganizationStatusFilter(),
      },
      role: {
        translationKey: 'organization.users.role',
        filterOptions: [
          {
            value: StaticRole.ORGANIZATION_ADMIN,
            representation: this.translocoService.translate(`tables.filter.role.${StaticRole.ORGANIZATION_ADMIN}`),
          },
          {
            value: StaticRole.ORGANIZATION_MEMBER,
            representation: this.translocoService.translate(`tables.filter.role.${StaticRole.ORGANIZATION_MEMBER}`),
          },
        ],
      },
    };
  }

  private getPFAFilter(): Filter<MarkantSelectOption> {
    return {
      state: {
        translationKey: 'organization.users.status.label',
        filterOptions: [
          ...this.getOrganizationStatusFilter(),
          {
            value: UserOrganizationStatus.BLOCKED,
            representation: this.translocoService.translate(`tables.filter.state.${UserOrganizationStatus.BLOCKED}`),
          },
          {
            value: UserOrganizationStatus.DELETED,
            representation: this.translocoService.translate(`tables.filter.state.${UserOrganizationStatus.DELETED}`),
          },
        ],
      },
      role: {
        translationKey: 'organization.users.role',
        filterOptions: [
          {
            value: StaticRole.ORGANIZATION_ADMIN,
            representation: this.translocoService.translate(`tables.filter.role.${StaticRole.ORGANIZATION_ADMIN}`),
          },
          {
            value: StaticRole.LEISTUNG_ADMIN,
            representation: this.translocoService.translate(`tables.filter.role.${StaticRole.LEISTUNG_ADMIN}`),
          },
          {
            value: StaticRole.PLATFORM_ADMIN,
            representation: this.translocoService.translate(`tables.filter.role.${StaticRole.PLATFORM_ADMIN}`),
          },
        ],
      },
    };
  }

  private setFilterOptions(): Observable<void> {
    this.filter = this.userRoles.includes(StaticRole.PLATFORM_ADMIN) ? this.getPFAFilter() : this.getOAFilter();
    const filterQueryParameter = buildFilterQueryParameter(['state', 'organizationId'], {
      state: ['BOOKED', 'TO_BE_CANCELLED'],
      organizationId: [this.organizationId],
    });

    return this.backofficeCartridgeService.getBookingsForOrganization(-1, -1, undefined, filterQueryParameter).pipe(
      catchError(_ => of(EMPTY_PAGE)),
      tap(page => {
        const variantIds: string[] = page.result.map(booking => booking.cartridgeVariantId);
        this.patchActiveFilters({
          'has-role-for-service':
            this.tableParams$.value.activeFilter['has-role-for-service']?.filter(variantId =>
              variantIds.includes(variantId)
            ) ?? [],
          'no-role-for-service':
            this.tableParams$.value.activeFilter['no-role-for-service']?.filter(variantId =>
              variantIds.includes(variantId)
            ) ?? [],
        });
      }),
      map(page => {
        if (!page.result.length) {
          return;
        }
        const variantOptions: MarkantSelectOption[] = page.result.map(({ cartridgeVariantId, cartridgeName }) => ({
          value: cartridgeVariantId,
          representation: cartridgeName,
        }));

        this.filter = {
          ...this.filter,
          'has-role-for-service': {
            translationKey: 'organization.users.has-role-for-service.label',
            filterOptions: variantOptions,
          },
          'no-role-for-service': {
            translationKey: 'organization.users.no-role-for-service.label',
            filterOptions: variantOptions,
          },
        };
      })
    );
  }
}
